import httpService from '../httpService';
import * as tagSlice from '../../redux/slices/tagsSlice';
import * as communityTagSlice from '../../redux/slices/communityTagsSlice';
import { store } from '../../redux/store';
import { TagState } from '../../types/Tag';
import {
  ConnectionType,
  assignTagToConnections,
  removeTagFromAllConnections,
  removeTagFromConnection,
} from '../../redux/slices/connectionsSlice';
import { removeTag } from '../../redux/slices/tagsSlice';

/**
 * @description Tag service
 * Handles all the tag related requests
 */
const tagService = {
  // disable lint warning for unused variables
  /* eslint-disable @typescript-eslint/no-unused-vars */
  /**
   * @description Creates a tag with the given name
   * @param tag - String: Name of the tag
   * Add the tag to the store
   */
  addTag: async (name: string, color: string): Promise<TagState> => {
    const dashboardId = store.getState().application.selectedDashboard;
    return httpService
      .post<TagState>(`/networks/dashboards/${dashboardId}/tags`, { name, color })
      .then(({ data }): TagState => {
        store.dispatch(tagSlice.setTags([data, ...store.getState().tags]));
        return data;
      });
  },
  /**
   * @description Fetches all the tags from the backend
   * Add the tags to the store
   */
  fetchTags: async (): Promise<void> => {
    const dashboardId = store.getState().application.selectedDashboard;
    await httpService.get(`/networks/dashboards/${dashboardId}/tags`).then(({ data }): void => {
      store.dispatch(tagSlice.setTags(data));
    });
  },
  /**
   * @description Deletes a tag with the given id
   * @param id - String: Id of the tag
   */
  deleteTag: async (id: string): Promise<void> => {
    try {
      const dashboardId = store.getState().application.selectedDashboard;
      await httpService.delete<TagState>(`/networks/dashboards/${dashboardId}/tags/${id}`).then((): void => {
        store.dispatch(removeTag(id));
        store.dispatch(removeTagFromAllConnections(id));
      });
    } catch (error) {
      console.error('Error deleting tag:', error);
    }
  },
  /**
   * @description Removes a tag from a connection
   * @param dashboardMemberIds - String: the id of the connection
   * @param tagId - String: the id of the tag
   * @param connectionType - ConnectionType: the type of the connection (MEMBER or EXTERNAL_MEMBER)
   * Finally, fetches all the connections again such that the labels are updated
   */
  removeTagFromConnection: async (dashboardMemberIds: number, tagId: string, connectionType: ConnectionType): Promise<void> => {
    try {
    const dashboardId = store.getState().application.selectedDashboard;
    await httpService
      .delete<TagState>(`/networks/dashboards/${dashboardId}/tags/${tagId}/assigned`, { data: { dashboardMemberIds: [dashboardMemberIds] } })
      .then(async (): Promise<void> => {
        store.dispatch(
          removeTagFromConnection({
            connection: dashboardMemberIds,
            tag: tagId,
            type: connectionType,
          }),
        );
      });
    } catch (error) {
      console.error('Error removing tag from connection:', error);
    }
  },

  /**
   * @description Assigns a tag to multiple connections
   * @param dashboardMemberIds - String[]: the ids of the connections to assign the tag to
   * @param tagId - String: the id of the tag to assign
   * @param connections - ConnectionIdentifier[]: the connection identifiers with type information
   * Finally, fetches all the connections again such that the labels are updated
   */
  assignTagToConnections: async (dashboardMemberIds: number[], tagId: string, connections: { dashboardMemberId: number; type: ConnectionType }[]): Promise<void> => {
    try {
    if (dashboardMemberIds.length === 0) return;
    
    const dashboardId = store.getState().application.selectedDashboard;
    await httpService
      .post<TagState>(`/networks/dashboards/${dashboardId}/tags/${tagId}/assigned`, { dashboardMemberIds })
      .then(() => {
        // Update Redux state for each connection with its correct type
        connections.forEach(connection => {
          store.dispatch(
            assignTagToConnections({
              connections: [connection.dashboardMemberId],
              tag: tagId,
              type: connection.type,
            }),
          );
        });
      });
    } catch (error) {
      console.error('Error assigning tag to connections:', error);
    }
  },

  /**
   * @description Fetches all community-scoped tags from the backend
   */
  fetchCommunityTags: async (): Promise<void> => {
    const dashboardId = store.getState().application.selectedDashboard;
    await httpService
      .get<TagState[]>(`/networks/dashboards/${dashboardId}/community-tags`)
      .then(({ data }): void => {
        store.dispatch(communityTagSlice.setCommunityTags(data));
      });
  },

  /**
   * @description Creates a community tag
   */
  addCommunityTag: async (name: string, color: string): Promise<TagState> => {
    const dashboardId = store.getState().application.selectedDashboard;
    return httpService
      .post<TagState>(`/networks/dashboards/${dashboardId}/community-tags`, { name, color })
      .then(({ data }): TagState => {
        store.dispatch(communityTagSlice.setCommunityTags([data, ...store.getState().communityTags]));
        return data;
      });
  },

  /**
   * @description Deletes a community tag
   */
  deleteCommunityTag: async (id: string): Promise<void> => {
    try {
      const dashboardId = store.getState().application.selectedDashboard;
      await httpService
        .delete(`/networks/dashboards/${dashboardId}/community-tags/${id}`)
        .then((): void => {
          store.dispatch(communityTagSlice.removeCommunityTag(id));
        });
    } catch (error) {
      console.error('Error deleting community tag:', error);
    }
  },

  /**
   * @description Assigns a community tag to a community
   */
  assignTagToCommunity: async (communityId: number, tagId: string): Promise<void> => {
    try {
      const dashboardId = store.getState().application.selectedDashboard;
      await httpService.post(
        `/networks/dashboards/${dashboardId}/community-tags/${tagId}/assigned`,
        { communityIds: [communityId] },
      );
    } catch (error) {
      console.error('Error assigning community tag:', error);
    }
  },

  /**
   * @description Removes a community tag from a community
   */
  removeTagFromCommunity: async (communityId: number, tagId: string): Promise<void> => {
    try {
      const dashboardId = store.getState().application.selectedDashboard;
      await httpService.delete(
        `/networks/dashboards/${dashboardId}/community-tags/${tagId}/assigned`,
        { data: { communityIds: [communityId] } },
      );
    } catch (error) {
      console.error('Error removing community tag:', error);
    }
  },
};

export default tagService;
